import React, { useState, useEffect } from 'react';
import { UserProfile } from '../types';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Settings, 
  LogOut, 
  Star, 
  Video, 
  History, 
  Truck,
  Bell  // NEW
} from 'lucide-react';

// Import all tabs
import { DashboardTab } from './admin-tabs/DashboardTab';
import DeliveryTimelineTab from './admin-tabs/DeliveryTimelineTab';
import { PackersTab } from './admin-tabs/PackersTab';
import { ReviewsTab } from './admin-tabs/ReviewsTab';
import InventoryTab from './admin-tabs/inventory/InventoryTab';
import { BillingTab } from './admin-tabs/BillingTab';
import { SettingsTab } from './admin-tabs/settings';
import { HistoryTab } from './admin-tabs/HistoryTab';
import { OrderUpdateTab } from './admin-tabs/order-update/OrderUpdateTab';  // NEW

interface AdminDashboardProps {
  user: UserProfile;
  onLogout: () => void;
}

// Define valid tab names
type TabID = 'dashboard' | 'packers' | 'billing' | 'settings' | 'history' | 'delivery-timeline' | 'inventory' | 'order-update';  // ADDED order-update

const AdminPanel: React.FC<AdminDashboardProps> = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState<TabID>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); 
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('code') && params.get('state')) {
      setActiveTab('settings');
    }
  }, []);

  const navItems = [
    { id: 'dashboard' as TabID, label: 'Dashboard', icon: LayoutDashboard },
    { id: 'history' as TabID, label: 'Order History', icon: History },
    { id: 'packers' as TabID, label: 'Packers', icon: Users },
    { id: 'delivery-timeline' as TabID, label: 'Delivery Timeline', icon: Truck },
    { id: 'order-update' as TabID, label: 'Order Update', icon: Bell },  // NEW
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'inventory' as TabID, label: 'Inventory', icon: Truck },
    { id: 'settings' as TabID, label: 'Settings', icon: Settings },
    { id: 'billing' as TabID, label: 'Billing', icon: CreditCard },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      
      {/* SIDEBAR */}
      <aside className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:relative md:translate-x-0 flex flex-col justify-between
      `}>
        <div>
            {/* Logo */}
            <div className="p-6 border-b border-slate-100">
                <div className="font-black text-xl text-indigo-600 flex items-center gap-2">
                    <Video className="w-8 h-8" /> PowerPack
                </div>
                <p className="text-xs text-slate-400 mt-1">Admin Console</p>
            </div>

            {/* Nav Items */}
            <nav className="p-4 space-y-1">
                {navItems.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => {
                            setActiveTab(item.id);
                            setIsSidebarOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                            activeTab === item.id 
                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                                : 'text-slate-500 hover:bg-slate-50 hover:text-indigo-600'
                        }`}
                    >
                        <item.icon size={20} />
                        {item.label}
                    </button>
                ))}
            </nav>
        </div>

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-3 mb-3 px-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
                    {user.name?.charAt(0) || 'A'}
                </div>
                <div className="overflow-hidden">
                    <p className="text-sm font-bold text-slate-700 truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate capitalize">{user.role}</p>
                </div>
            </div>
            <button 
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-white hover:text-red-600 transition-colors"
            >
                <LogOut size={16} /> Logout
            </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto bg-gray-50/50">
        
        {/* Mobile Header */}
        <div className="md:hidden bg-white border-b border-slate-200 p-4 flex justify-between items-center sticky top-0 z-30">
            <div className="font-bold text-indigo-600 flex items-center gap-2">
                <Video className="w-6 h-6" /> PowerPack
            </div>
            <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-slate-600">
                <span className="sr-only">Menu</span>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
        </div>

        <div className="p-4 md:p-8 max-w-7xl mx-auto">
            {/* Tabs with user prop */}
            {activeTab === 'dashboard' && <DashboardTab user={user} />}
            {activeTab === 'history' && <HistoryTab user={user} />} 
            {activeTab === 'packers' && <PackersTab user={user} />}
            {activeTab === 'reviews' && <ReviewsTab user={user} />}
            {activeTab === 'billing' && <BillingTab user={user} />}
            {activeTab === 'settings' && <SettingsTab user={user} />}
            {activeTab === 'delivery-timeline' && <DeliveryTimelineTab user={user} />}
            {activeTab === 'inventory' && <InventoryTab adminId={user.id} />}
            {activeTab === 'order-update' && <OrderUpdateTab user={user} />}  {/* NEW */}
        </div>
      </main>

    </div>
  );
};

export default AdminPanel;
